import classNames from 'classnames'
import { useTranslation } from 'next-i18next'
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
  DetailedHTMLProps,
  useId,
} from 'react'

import Image from '@src/quince-site-common/src/domains/Image'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import useIntersect from '@src/quince-site-common/src/hooks/useIntersect'
import getUserDeviceCategory, { DEVICES } from '@src/quince-site-common/src/utils/getUserDeviceCategory'
import isBrowser from '@src/quince-site-common/src/utils/isBrowser'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackInternalEvent } from '@src/telemetry/track'
import { throttle, debounce } from '@src/utils/functions/utils'

import { ImageProps } from '../image'

// Icons
import BlackMute from './icons/blackMute.inline.svg'
import BlackPlay from './icons/blackPlay.inline.svg'
import MuteIcon from './icons/muteIcon.inline.svg'
import MuteIconMobile from './icons/muteIconMobile.inline.svg'
import PauseIcon from './icons/pauseIcon.inline.svg'
import PauseIconMobile from './icons/pauseIconMobile.inline.svg'
import VideoPlayIcon from './icons/videoPlayIcon.inline.svg'
import LimitedControlButtons from './limitedControlButtons'
// Styles
import styles from './video.module.scss'

const PROGRESS_UPDATE_INTERVAL_IN_MILLISECONDS = 1000
export const HOVER_DEBOUNCE_TIME_MOBILE_IN_MILLISECONDS = 500

const _noop = () => {
  // Empty function placeholder
}
export enum VIDEO_LOADING_TYPE {
  Lazy = 'lazy',
  Eager = 'eager',
}

export enum VIDEO_PRELOAD_TYPE {
  Auto = 'auto',
  Metadata = 'metadata',
  None = 'none',
}

export interface VideoProps extends DetailedHTMLProps<React.VideoHTMLAttributes<HTMLVideoElement>, HTMLVideoElement> {
  id: string
  url: string
  contentType: string
  title?: string
  preload?: VIDEO_PRELOAD_TYPE
  loadingType?: VIDEO_LOADING_TYPE
  className?: string
  wrapperClassName?: string
  posterImage?: Image | ImageProps
  autoplay?: boolean
  tracker?: string
  limitedControls?: boolean
  playOnHover?: boolean
  hideVideoPlayIcon?: boolean
  onVideoClick?: () => void
  onVideoEnd?: () => void
  trackingProperties?: Record<string, unknown>
}

export const getRemainingVideoTime = (currentTimeInSeconds: number, videoDurationInSeconds?: number) => {
  const defaultTime = '00:00'
  if (videoDurationInSeconds) {
    const diff = videoDurationInSeconds - currentTimeInSeconds
    const minutesVal = Math.floor(diff / 60)
    const secondsVal = Math.round(diff % 60)
    const minutes = minutesVal < 10 ? `0${minutesVal}` : minutesVal
    const seconds = secondsVal < 10 ? `0${secondsVal}` : secondsVal
    return `${minutes}:${seconds}`
  }
  return defaultTime
}

const handleVideoTimeChange = (
  e: React.ChangeEvent<HTMLInputElement>,
  videoEl: HTMLVideoElement | null,
  setVideoCurrentTime: (time: number) => void,
) => {
  const newTime = parseFloat(e.target.value)
  if (videoEl) {
    videoEl.currentTime = newTime
  }
  setVideoCurrentTime(newTime)
}

const Video = forwardRef(
  (
    {
      url,
      contentType,
      autoplay = false,
      loop = false,
      muted = false,
      loadingType = VIDEO_LOADING_TYPE.Lazy,
      preload = VIDEO_PRELOAD_TYPE.None,
      disablePictureInPicture = true,
      controls = false,
      limitedControls = false,
      title,
      className,
      posterImage,
      tracker,
      wrapperClassName,
      hideVideoPlayIcon = false,
      onVideoClick,
      onVideoEnd = _noop,
      trackingProperties,
    }: VideoProps,
    ref,
  ) => {
    const { t } = useTranslation('common')
    const { deviceType } = useDeviceInfo()
    const isMobile = deviceType === DEVICE_TYPES.MOBILE
    const sliderId = useId()

    const [isAutoPlayStarted, setIsAutoPlayStarted] = useState(false)
    const [isPlaying, setIsPlaying] = useState(false)
    const [isMuted, setIsMuted] = useState(muted)
    const [videoCurrentTime, setVideoCurrentTime] = useState(0)
    const [isPlaysInline, setIsPlaysInline] = useState(false)

    const [videoRefFn, isIntersecting, intersectedElement] = useIntersect(null, '400px', 0, true)
    const videoEl = intersectedElement as HTMLVideoElement | null

    const isTrackedRef = useRef<boolean>(false)
    const videoContainerRef = useRef<HTMLDivElement>(null)
    const progressBarTickerRef = useRef<HTMLSpanElement>(null)
    const progressBarWrapperRef = useRef<HTMLDivElement>(null)
    const videoCustomControlsRef = useRef<HTMLDivElement>(null)
    const progressRef = useRef<HTMLProgressElement>(null)

    const showControls = controls && !limitedControls && (preload !== VIDEO_PRELOAD_TYPE.Metadata || autoplay)
    const showLimitedControls = !controls && limitedControls && (preload !== VIDEO_PRELOAD_TYPE.Metadata || autoplay)

    useEffect(() => {
      if (videoEl && autoplay) {
        if (isIntersecting) {
          if (!isAutoPlayStarted) {
            const sourceElement = videoEl.querySelector('source')
            if (sourceElement) {
              // @i18n-ignore
              sourceElement.src = `https:${url}`
            }
            const playPromise = videoEl.play()
            playPromise
              .then(() => {
                setIsPlaying(true)
                setIsAutoPlayStarted(true)
              })
              .catch((err: unknown) => {
                if (err instanceof Error) {
                  console.log('Autoplay for the video failed : ', err.message)
                }
              })
          } else {
            setIsPlaying(true)
          }
        } else {
          setIsPlaying(false)
          setIsAutoPlayStarted(false)
          videoEl.currentTime = 0
          if (videoEl.played.length) {
            videoEl.pause()
          }
        }
      }
    }, [autoplay, isAutoPlayStarted, isIntersecting, url, videoEl])

    useEffect(() => {
      setIsPlaysInline((isBrowser() && getUserDeviceCategory() === DEVICES.iPhone) || autoplay)
    }, [autoplay])

    const handleControlsDisplay = useCallback(() => {
      if (videoCustomControlsRef.current) {
        const isDisplayed = videoCustomControlsRef.current.style.opacity === '1'
        videoCustomControlsRef.current.style.opacity = isDisplayed ? '0' : '1'
      }
    }, [])
    const { current: debouncedControlsDisplay } = useRef(
      debounce(handleControlsDisplay, HOVER_DEBOUNCE_TIME_MOBILE_IN_MILLISECONDS),
    )

    const handleVideoPausePlay = useCallback(() => {
      if (isPlaying || (videoEl && !videoEl.paused)) {
        videoEl?.pause()
        setIsPlaying(false)
      } else {
        const playPromise = videoEl?.play()
        if (playPromise !== undefined) {
          playPromise
            .then(() => {
              setIsPlaying(true)
            })
            .catch((err: unknown) => {
              if (err instanceof Error) {
                console.log('Playing the video failed : ', err.message)
              }
            })
        }
      }
    }, [isPlaying, setIsPlaying, videoEl])

    const handlePause = useCallback(() => {
      if (isPlaying || (videoEl && !videoEl.paused)) {
        videoEl?.pause()
        setIsPlaying(false)
      }
    }, [isPlaying, setIsPlaying, videoEl])

    const handlePlay = useCallback(() => {
      if (!isPlaying && videoEl?.paused) {
        const playPromise = videoEl.play()
        playPromise
          .then(() => {
            setIsPlaying(true)
          })
          .catch((err: unknown) => {
            if (err instanceof Error) {
              console.log('Playing the video failed : ', err.message)
            }
          })
      }
    }, [isPlaying, setIsPlaying, videoEl])

    const handleVideoClick = useCallback(() => {
      if (isMobile) {
        if (!isPlaying) {
          handleVideoPausePlay()
        } else {
          debouncedControlsDisplay()
        }
      } else {
        handleVideoPausePlay()
      }
    }, [debouncedControlsDisplay, handleVideoPausePlay, isMobile, isPlaying])

    const handleVideoMuteUnmute = useCallback(() => {
      if (videoEl) {
        videoEl.muted = !videoEl.muted
        setIsMuted(videoEl.muted)
      }
    }, [videoEl])

    useImperativeHandle(ref, () => {
      return {
        handleMuteUnmute() {
          handleVideoMuteUnmute()
        },
        handlePause,
        handlePlay,
      }
    }, [handleVideoMuteUnmute, handlePause, handlePlay])

    const trackIfVideoIsSeen = (videoCurrentTime: number) => {
      if (tracker && !isTrackedRef.current && videoCurrentTime >= 1) {
        isTrackedRef.current = true
        trackInternalEvent(TELEMETRY_EVENT_NAMES.IS_VIDEO_WATCHED, {
          label: 'video_watched',
          value: tracker,
          context: trackingProperties,
        })
      }
    }

    const updateProgressBar = useCallback((val: number) => {
      if (progressRef.current) {
        progressRef.current.value = val
      }
      if (progressBarTickerRef.current && progressBarWrapperRef.current) {
        const pos = (val / 100) * progressBarWrapperRef.current.clientWidth
        progressBarTickerRef.current.style.left = `${pos}px`
      }
    }, [])

    const handleVideoTimeUpdate = (videoElement?: HTMLVideoElement) => {
      if (videoElement) {
        updateProgressBar(
          videoElement.currentTime ? Math.round((videoElement.currentTime / videoElement.duration) * 100) : 0,
        )
        trackIfVideoIsSeen(videoElement.currentTime)
        setVideoCurrentTime(videoElement.currentTime || 0)
      }
    }

    const { current: throttledTimeUpdate } = useRef(
      throttle(handleVideoTimeUpdate, PROGRESS_UPDATE_INTERVAL_IN_MILLISECONDS),
    )

    const pauseIcon = isMobile ? <PauseIconMobile /> : <PauseIcon />
    const muteIcon = isMobile ? <MuteIconMobile /> : <MuteIcon />
    const muteButton = (
      <button
        className={styles.controls__mute}
        type="button"
        onClick={handleVideoMuteUnmute}
        aria-label={isMuted ? t('unmute_video') : t('mute_video')}
      >
        {isMuted ? (
          <span className={styles.controls__controlButtonsWrapper} data-testid={'VideoElMutedButton'}>
            {muteIcon}
          </span>
        ) : (
          <span className={styles.controls__controlButtonsWrapper} data-testid={'VideoElNonMutedButton'}>
            <BlackMute />
          </span>
        )}
      </button>
    )

    const getPosterImageUrl = useCallback(() => {
      if (!posterImage?.url) {
        return undefined
      }
      // @i18n-ignore
      const MOBILE_QUERY_PARAMS = '?w=1600&q=95&fm=webp'
      // @i18n-ignore
      const DESKTOP_QUERY_PARAMS = '?w=1600&q=95&fm=webp'
      return `https:${posterImage.url}${isMobile ? MOBILE_QUERY_PARAMS : DESKTOP_QUERY_PARAMS}`
    }, [isMobile, posterImage?.url])

    return (
      <div className={classNames(styles.videoWrapper, wrapperClassName)} ref={videoContainerRef}>
        <video
          ref={e => {
            videoRefFn(e)
          }}
          title={title}
          autoPlay={autoplay}
          loop={loop}
          muted={muted}
          // @i18n-ignore
          data-src={`https:${url}`}
          preload={preload}
          controls={false}
          className={classNames(className, {
            [styles.videoWrapper__video ?? '']: !showLimitedControls,
          })}
          disablePictureInPicture={disablePictureInPicture}
          playsInline={isPlaysInline}
          height={'100%'}
          width={'100%'}
          poster={getPosterImageUrl()}
          onClick={onVideoClick ?? handleVideoClick}
          onEnded={() => {
            onVideoEnd()
            setIsPlaying(false)
          }}
          onTimeUpdate={() => {
            if (videoEl) {
              throttledTimeUpdate(videoEl)
            }
          }}
        >
          <source
            // @i18n-ignore
            data-src={`https:${url}`}
            type={contentType}
            // @i18n-ignore
            src={loadingType === VIDEO_LOADING_TYPE.Eager ? `https:${url}` : undefined}
          />
        </video>
        {showLimitedControls && <LimitedControlButtons isPlaying={isPlaying} onClick={handleVideoPausePlay} />}
        {!isMobile && !autoplay && !isPlaying && !hideVideoPlayIcon && (
          <button className={styles.videoWrapper__play} onClick={handleVideoPausePlay} aria-label={t('play_video')}>
            <VideoPlayIcon />
          </button>
        )}
        {isMobile && !autoplay && isAutoPlayStarted && !hideVideoPlayIcon && (videoEl?.paused ?? !isPlaying) && (
          <button className={styles.videoWrapper__play} onClick={handleVideoPausePlay} aria-label={t('play_video')}>
            <VideoPlayIcon />
          </button>
        )}
        {showControls && (
          <div className={styles.controls} ref={videoCustomControlsRef}>
            <button
              className={styles.controls__playPause}
              type="button"
              onClick={handleVideoPausePlay}
              aria-label={isPlaying ? t('pause_video') : t('play_video')}
            >
              {isPlaying ? (
                <span className={styles.controls__controlButtonsWrapper}>{pauseIcon}</span>
              ) : (
                <span className={styles.controls__controlButtonsWrapper}>
                  <BlackPlay />
                </span>
              )}
            </button>
            {!isMobile && muteButton}
            <div className={styles.controls__progressWrapper} ref={progressBarWrapperRef}>
              <label htmlFor={sliderId} className="sr-only"></label>
              <input
                id={sliderId}
                type="range"
                min={0}
                max={videoEl?.duration ?? 0}
                step={0.1}
                value={videoCurrentTime}
                onChange={e => {
                  handleVideoTimeChange(e, videoEl, setVideoCurrentTime)
                }}
                className={styles.controls__slider}
                data-testid="video-range-slider"
              />
            </div>
            {isMobile && muteButton}
            <div className={styles.controls__timeLeft}>
              <span>-{getRemainingVideoTime(videoCurrentTime, videoEl?.duration)}</span>
            </div>
          </div>
        )}
      </div>
    )
  },
)

// @i18n-ignore
const VIDEO_DISPLAY_NAME = 'Video'
Video.displayName = VIDEO_DISPLAY_NAME

export default Video
